/**
 * The short name and the short address of a location (QA 4c7a2a76; Shardul,
 * 07 Oct 26): gmb_locations.location_short_name and location_short_address,
 * the two the fly-app Locations page edits. The connect questions ask them
 * first, filled in and editable, before the agent is switched on
 * (ConnectQuestionsPanel), and Location settings changes them later
 * (LocationSettingsPanel). The agent writes the descriptions, the service
 * descriptions and the searches it tracks with them, and prints them on post
 * images.
 *
 * The words both places show, the caps the server holds them to, and what a
 * save sends. Pure, for the tests (shortNames.test.ts).
 */

export const SHORT_NAME_LABEL = 'Short name';
export const SHORT_ADDRESS_LABEL = 'Short address';
export const SHORT_NAME_PLACEHOLDER = 'e.g. Sweet Scoop';
export const SHORT_ADDRESS_PLACEHOLDER = 'e.g. Baner, Pune';

/** Why they are asked, in one breath (the ticket: "a short note why it is there"). */
export const SHORT_NAMES_WHY =
  'The agent writes your Google description, your service descriptions and the searches it tracks with this name and area, and prints them on your post images.';

/** Longest short name that fits every post design whole (the server's SHORT_NAME_MAX, _shared/place-names.ts). */
export const SHORT_NAME_FIT = 18;
/** Longest short name the server keeps (SHORT_NAME_HARD_MAX). */
export const SHORT_NAME_LIMIT = 48;
/** Longest short address that fits under the name on a post image (SHORT_ADDRESS_MAX). */
export const SHORT_ADDRESS_FIT = 28;
/** Longest short address the server keeps (connect-questions-shape.ts SHORT_ADDRESS_HARD_MAX). */
export const SHORT_ADDRESS_LIMIT = 56;

export interface ShortNames {
  shortName: string;
  shortAddress: string;
}

/** One line, single spaces: what a save sends. */
function oneLine(raw: string): string {
  return String(raw ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * What a save sends, cleaned, or null while either is empty or longer than
 * the server keeps: both are needed, since the agent writes with both.
 */
export function shortNamesBody(names: ShortNames): { short_name: string; short_address: string } | null {
  const shortName = oneLine(names.shortName);
  const shortAddress = oneLine(names.shortAddress);
  if (!shortName || !shortAddress) return null;
  if (shortName.length > SHORT_NAME_LIMIT || shortAddress.length > SHORT_ADDRESS_LIMIT) return null;
  return { short_name: shortName, short_address: shortAddress };
}

/** The line under a box: too long to keep, or past what fits a post design. Null when it fits. */
export function fitNote(value: string, fit: number, limit: number): string | null {
  const n = oneLine(value).length;
  if (n > limit) return `Keep it to ${limit} letters or fewer.`;
  if (n > fit) return `Longer than ${fit} letters may not fit every post design.`;
  return null;
}

/** Said when a save leaves the location as it was: a reader who may not change it (an agency reader). */
export const SHORT_NAMES_NOT_SAVED = 'This account cannot change this location. Ask its owner to change it.';
